import classnames from 'classnames'
import { KeyboardEvent, MouseEvent, KeyboardEventHandler } from 'react'
import { User } from '@opallabs/concepts'
import { Avatar as LapidaryAvatar } from '../Avatar'
import { Button, ButtonSize } from '../Button'
import { Pill } from '../Pill'
import * as styles from './styles'

export type ClassNameProp = {
  className?: string
}

export const Root: React.FC<React.PropsWithChildren<ClassNameProp>> = ({
  children,
  className,
}) => {
  return (
    <ul
      role="menu"
      className={classnames('lapidary-history', className)}
      css={styles.list}
    >
      {children}
    </ul>
  )
}
Root.displayName = 'Lapidary: Root ← History'

export type HistoryItemProps = {
  selected?: boolean
  disabled?: boolean
  onSelect?(e: MouseEvent | KeyboardEvent): void
} & ClassNameProp

export const Item: React.FC<React.PropsWithChildren<HistoryItemProps>> = ({
  children,
  selected = false,
  disabled = false,
  className,
  onSelect,
}) => {
  const handleKeyUp: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' || e.key === 'Space') {
      onSelect?.(e)
    }
  }
  return (
    <li
      aria-disabled={disabled}
      role="menuitem"
      className={classnames('lapidary-history-item', className)}
      css={styles.item({ selected })}
      onClick={disabled ? undefined : onSelect}
      onKeyUp={handleKeyUp}
    >
      {children}
    </li>
  )
}
Item.displayName = 'Lapidary: Item ← History'

export type AvatarProps = {
  user: User
}

export const Avatar: React.FC<AvatarProps> = ({ user }) => {
  return (
    <div>
      <LapidaryAvatar fullName={user.name} src={user.asset?.url} size={20} />
    </div>
  )
}
Avatar.displayName = 'Lapidary: Avatar ← History'

export const Title: React.FC<React.PropsWithChildren<ClassNameProp>> = ({
  children,
  className,
}) => {
  return (
    <span
      className={classnames('lapidary-history-title', className)}
      css={styles.title}
    >
      {children}
    </span>
  )
}
Title.displayName = 'Lapidary: Title ← History'

export const SubTitle: React.FC<React.PropsWithChildren<ClassNameProp>> = ({
  children,
  className,
}) => {
  return (
    <span
      className={classnames('lapidary-history-subtitle', className)}
      css={styles.subtitle}
    >
      {children}
    </span>
  )
}
SubTitle.displayName = 'Lapidary: SubTitle ← History'

export const Details = ({ children }: { children?: React.ReactNode }) => {
  return <div css={styles.textContainer}>{children}</div>
}
Details.displayName = 'Lapidary: Details ← History'

export type RestorationActionProps = {
  current?: boolean
  canRestore?: boolean
  onRestore?(e: MouseEvent): void
}

export const RestorationAction: React.FC<RestorationActionProps> = ({
  current,
  canRestore = true,
  onRestore,
}) => {
  if (current) {
    return (
      <Pill
        text="Current"
        color="green"
        size="xsmall"
        css={styles.currentPill}
        solid
      />
    )
  }
  if (canRestore) {
    return (
      <Button
        name="restore"
        variant="ghostBlue"
        onClick={onRestore}
        buttonText="Restore"
        size={ButtonSize.Small}
      />
    )
  }

  return null
}
RestorationAction.displayName = 'Lapidary: RestorationAction ← History'
